import React, { createContext, useContext, useEffect, useState } from 'react'
import { useAnalytics } from '@/hooks/useAnalytics'

interface AnalyticsContextType {
  trackEvent: (eventType: string, eventData?: Record<string, any>) => void
  trackUserAction: (action: string, data?: Record<string, any>) => void
  trackTaskCompletion: (taskId: string, taskType: string) => void
  trackSlimeEvolution: (fromStage: string, toStage: string) => void
  trackQuestCompletion: (questId: string, questType: string) => void
  sessionId: string
}

const AnalyticsContext = createContext<AnalyticsContextType | null>(null)

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext)
  if (!context) {
    throw new Error('useAnalyticsContext must be used within AnalyticsProvider')
  }
  return context
}

interface AnalyticsProviderProps {
  children: React.ReactNode
  userId?: string
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ 
  children, 
  userId 
}) => {
  const [sessionId] = useState(() => {
    const existing = sessionStorage.getItem('slimehabit_session_id')
    if (existing) return existing
    
    const newId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    sessionStorage.setItem('slimehabit_session_id', newId)
    return newId
  })

  const analytics = useAnalytics({ userId, sessionId })

  const value: AnalyticsContextType = {
    trackEvent: (eventType: string, eventData?: Record<string, any>) => 
      analytics.trackEvent({ eventType, eventData }),
    trackUserAction: analytics.trackUserAction,
    trackTaskCompletion: analytics.trackTaskCompletion,
    trackSlimeEvolution: analytics.trackSlimeEvolution,
    trackQuestCompletion: analytics.trackQuestCompletion,
    sessionId: analytics.sessionId
  }

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  )
}